import React, { useState, useContext } from "react";
import { MyContext } from "../../App";
import { postData } from "../../utils/api";

export default function ContactForm() {
  const context = useContext(MyContext);
  const [form, setForm] = useState({
    fullName: "",
    email: "",
    phone: "",
    message: "",
  });
  const [isLoading, setIsLoading] = useState(false);

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);

    try {
      const response = await postData("/api/contacts", form);

      if (response && response.message) {
        context.setAlertBox({
          open: true,
          error: false,
          msg: response.message || "Message sent successfully! We'll get back to you soon.",
        });
        setForm({ fullName: "", email: "", phone: "", message: "" });
      } else {
        throw new Error("Failed to send message");
      }
    } catch (err) {
      console.error("Contact submission error:", err);
      context.setAlertBox({
        open: true,
        error: true,
        msg: "Failed to send message. Please try again later.",
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div
      className="bg-green-50 p-4 md:p-8"
      style={{ fontFamily: "'Inter', sans-serif" }}
    >
      <div className="contact-container max-w-6xl mx-auto my-6 p-4 md:p-8 flex flex-col gap-6 bg-white rounded-2xl border border-green-200 shadow-lg">
        <div className="contact-header text-center mb-3">
          <h1 className="text-2xl md:text-3xl font-bold text-green-800 mb-2">
            Get In Touch
          </h1>
          <p className="text-gray-600 text-sm md:text-base leading-relaxed max-w-xl mx-auto">
            We'd love to hear from you. Send us a message and we'll respond as
            soon as possible.
          </p>
        </div>

        <div className="contact-content flex flex-col md:flex-row gap-6 justify-center items-start">
          <div className="contact-info flex-1 flex flex-col gap-3 min-w-[260px]">
            <div className="info-card bg-gray-50 border border-gray-200 rounded-xl p-4 text-center transition-transform duration-200 hover:-translate-y-1 hover:shadow-lg">
              <div className="info-icon text-2xl mb-2 text-green-600">📍</div>
              <h3 className="text-base font-semibold mb-1 text-green-800">
                Our Location
              </h3>
              <p className="text-sm text-gray-500">Pune, Maharashtra, India</p>
            </div>
            <div className="info-card bg-gray-50 border border-gray-200 rounded-xl p-4 text-center transition-transform duration-200 hover:-translate-y-1 hover:shadow-lg">
              <div className="info-icon text-2xl mb-2 text-green-600">📞</div>
              <h3 className="text-base font-semibold mb-1 text-green-800">
                Call Us
              </h3>
              <p className="text-sm text-gray-500">+91 9512707825</p>
            </div>
            <div className="info-card bg-gray-50 border border-gray-200 rounded-xl p-4 text-center transition-transform duration-200 hover:-translate-y-1 hover:shadow-lg">
              <div className="info-icon text-2xl mb-2 text-green-600">✉️</div>
              <h3 className="text-base font-semibold mb-1 text-green-800">
                Email Us
              </h3>
              <p className="text-sm text-gray-500">
                krishnachaudhari0340@gmail.com
              </p>
            </div>
          </div>

          <div className="contact-form flex-[1.2] min-w-[300px] bg-white rounded-xl p-0 md:p-6 flex flex-col gap-4">
            <form onSubmit={handleSubmit} className="flex flex-col gap-2">
              <div className="form-group flex flex-col gap-0.5">
                <label
                  htmlFor="fullName"
                  className="font-medium text-gray-800 text-sm"
                >
                  Full Name{" "}
                  <span className="required text-red-500 ml-1">*</span>
                </label>
                <input
                  id="fullName"
                  type="text"
                  name="fullName"
                  value={form.fullName}
                  onChange={handleChange}
                  placeholder="Enter your full name"
                  disabled={isLoading}
                  required
                  className="form-input w-full p-2.5 border border-gray-300 rounded-lg text-sm bg-gray-50 transition-all duration-200 focus:border-green-600 focus:outline-none focus:ring-2 focus:ring-green-600/30"
                />
              </div>

              <div className="form-group flex flex-col gap-0.5">
                <label
                  htmlFor="email"
                  className="font-medium text-gray-800 text-sm"
                >
                  Email Address{" "}
                  <span className="required text-red-500 ml-1">*</span>
                </label>
                <input
                  id="email"
                  type="email"
                  name="email"
                  value={form.email}
                  onChange={handleChange}
                  placeholder="Enter your email address"
                  disabled={isLoading}
                  required
                  className="form-input w-full p-2.5 border border-gray-300 rounded-lg text-sm bg-gray-50 transition-all duration-200 focus:border-green-600 focus:outline-none focus:ring-2 focus:ring-green-600/30"
                />
              </div>

              <div className="form-group flex flex-col gap-0.5">
                <label
                  htmlFor="phone"
                  className="font-medium text-gray-800 text-sm"
                >
                  Phone Number
                </label>
                <input
                  id="phone"
                  type="tel"
                  name="phone"
                  value={form.phone}
                  onChange={handleChange}
                  placeholder="Enter your phone number"
                  maxLength={15}
                  disabled={isLoading}
                  className="form-input w-full p-2.5 border border-gray-300 rounded-lg text-sm bg-gray-50 transition-all duration-200 focus:border-green-600 focus:outline-none focus:ring-2 focus:ring-green-600/30"
                />
              </div>

              <div className="form-group flex flex-col gap-0.5">
                <label
                  htmlFor="message"
                  className="font-medium text-gray-800 text-sm"
                >
                  Message <span className="required text-red-500 ml-1">*</span>
                </label>
                <textarea
                  id="message"
                  name="message"
                  value={form.message}
                  onChange={handleChange}
                  placeholder="Tell us how we can help you..."
                  rows={4}
                  disabled={isLoading}
                  required
                  className="form-textarea w-full p-2.5 border border-gray-300 rounded-lg text-sm bg-gray-50 transition-all duration-200 focus:border-green-600 focus:outline-none focus:ring-2 focus:ring-green-600/30 min-h-[100px] resize-y"
                />
              </div>

              <button
                type="submit"
                disabled={isLoading}
                className="submit-btn bg-green-700 text-white w-full py-0 rounded-lg font-semibold text-sm cursor-pointer transition-all duration-200 flex items-center justify-center gap-2 hover:bg-green-800 hover:-translate-y-0.5 disabled:bg-green-300 disabled:cursor-not-allowed mt-2"
                style={{ height: '48px', minHeight: '48px' }}
              >
                {isLoading ? (
                  <>
                    <span className="spinner border-2 border-white/40 border-t-white rounded-full w-4 h-4 inline-block animate-spin"></span>
                    Sending...
                  </>
                ) : (
                  "Send Message"
                )}
              </button>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
}